import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { Leaf } from 'lucide-react';
export function FloatingParticles({
  count = 20,
  color = 'bg-gold'
}) {
  return <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-126">
      {[...Array(count)].map((_, i) => <motion.div key={i} className={`absolute rounded-full ${color}`} style={{
      width: Math.random() * 4 + 2 + 'px',
      height: Math.random() * 4 + 2 + 'px',
      left: Math.random() * 100 + '%',
      top: Math.random() * 100 + '%',
      opacity: 0
    }} animate={{
      y: [0, -Math.random() * 150 - 50],
      x: [0, Math.random() * 60 - 30],
      opacity: [0, Math.random() * 0.5 + 0.2, 0]
    }} transition={{
      duration: Math.random() * 10 + 15,
      repeat: Infinity,
      ease: 'linear',
      delay: Math.random() * 10
    }} data-id="element-127" />)}
    </div>;
}
export function BotanicalDivider() {
  return <div className="flex items-center justify-center gap-4 my-16 opacity-60" data-id="element-128">
      <motion.div initial={{
      scaleX: 0
    }} whileInView={{
      scaleX: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      ease: 'easeOut'
    }} className="h-[1px] w-24 md:w-48 bg-gradient-to-r from-transparent to-sage-dark origin-right" data-id="element-129"></motion.div>
      <motion.div initial={{
      rotate: -45,
      opacity: 0
    }} whileInView={{
      rotate: 0,
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8,
      delay: 0.5
    }} data-id="element-130">
        <Leaf className="w-5 h-5 text-sage-dark" data-id="element-131" />
      </motion.div>
      <motion.div initial={{
      scaleX: 0
    }} whileInView={{
      scaleX: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      ease: 'easeOut'
    }} className="h-[1px] w-24 md:w-48 bg-gradient-to-l from-transparent to-sage-dark origin-left" data-id="element-132"></motion.div>
    </div>;
}
export function ConstellationCircle({
  className = ''
}) {
  return <motion.svg viewBox="0 0 200 200" className={`w-full h-full opacity-10 ${className}`} animate={{
    rotate: 360
  }} transition={{
    duration: 200,
    repeat: Infinity,
    ease: 'linear'
  }} data-id="element-133">
      <circle cx="100" cy="100" r="90" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="4 4" data-id="element-134" />
      <circle cx="100" cy="10" r="3" fill="currentColor" data-id="element-135" />
      <circle cx="163" cy="36" r="4" fill="currentColor" data-id="element-136" />
      <circle cx="190" cy="100" r="2" fill="currentColor" data-id="element-137" />
      <circle cx="163" cy="164" r="5" fill="currentColor" data-id="element-138" />
      <circle cx="100" cy="190" r="3" fill="currentColor" data-id="element-139" />
      <circle cx="36" cy="164" r="2" fill="currentColor" data-id="element-140" />
      <circle cx="10" cy="100" r="4" fill="currentColor" data-id="element-141" />
      <circle cx="36" cy="36" r="3" fill="currentColor" data-id="element-142" />
      <path d="M100 10 L163 36 L190 100 L163 164 L100 190 L36 164 L10 100 L36 36 Z" fill="none" stroke="currentColor" strokeWidth="0.5" data-id="element-143" />
      <path d="M100 10 L190 100 L100 190 L10 100 Z" fill="none" stroke="currentColor" strokeWidth="0.2" data-id="element-144" />
    </motion.svg>;
}
export function TreeMotif({
  className = 'w-16 h-16 text-sage-dark'
}) {
  return <motion.svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className={className} initial={{
    pathLength: 0,
    opacity: 0
  }} whileInView={{
    pathLength: 1,
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    duration: 2,
    ease: 'easeInOut'
  }} data-id="element-145">
      <path d="M12 22v-8" data-id="element-146" />
      <path d="M12 14c-2-2-4-4-4-6s2-4 4-4 4 2 4 4-2 4-4 6z" data-id="element-147" />
      <path d="M12 14c-3 0-6-2-6-5" data-id="element-148" />
      <path d="M12 14c3 0 6-2 6-5" data-id="element-149" />
      <path d="M12 22c-2 0-4-1-4-3" data-id="element-150" />
      <path d="M12 22c2 0 4-1 4-3" data-id="element-151" />
    </motion.svg>;
}
export function AnimatedCounter({
  value,
  suffix = '',
  className = ''
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (inView) {
      const controls = animate(0, value, {
        duration: 2,
        ease: 'easeOut',
        onUpdate(v) {
          setCount(Math.floor(v));
        }
      });
      return () => controls.stop();
    }
  }, [value, inView]);
  return <span ref={ref} className={className} data-id="element-152">
      {count}
      {suffix}
    </span>;
}
export function SectionNumber({
  number,
  color = 'text-forest-dark'
}) {
  return <div className="absolute top-10 left-10 md:left-20 opacity-[0.03] pointer-events-none select-none z-0" data-id="element-153">
      <span className={`text-[12rem] md:text-[16rem] font-serif font-bold ${color} leading-none`} data-id="element-154">
        {number}
      </span>
    </div>;
}
export function CornerDecorations({
  color = 'text-sage-light/30'
}) {
  return <>
      <svg className={`absolute top-4 left-4 w-12 h-12 ${color} pointer-events-none`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-155">
        <path d="M2 22V2h20" data-id="element-156" />
        <circle cx="6" cy="6" r="2" data-id="element-157" />
      </svg>
      <svg className={`absolute bottom-4 right-4 w-12 h-12 ${color} pointer-events-none rotate-180`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-158">
        <path d="M2 22V2h20" data-id="element-159" />
        <circle cx="6" cy="6" r="2" data-id="element-160" />
      </svg>
    </>;
}